import { createBrowserRouter } from "react-router-dom";
import Dashboard from "@pages/Dashboard";
import Layout from "../components/Layout";
import Home from "../pages/SignIn";
import NotFound from "../pages/NotFound";
import EmailVerification from "../pages/EmailVerification";
import EmailVerificationSuccess from "../pages/EmailVerificationSuccess";
import ForgetPassword from "../pages/ForgetPassword";
import ResetPassword from "../pages/ResetPassword";
import SignUp from "../pages/SignUp";
import PhoneVerification from "../pages/PhoneVerification";
import MyAccount from "../pages/MyAccount";
import EditMyAccount from "../pages/EditMyAccount";
import SetNewPassword from "../pages/SetNewPassword";
import ChangePassword from "@/pages/ChangePassword";
import Reports from "../pages/Reports";
import AddReport from "../pages/Reports/components/AddReport";
import EditReport from "../pages/Reports/components/EditReport";
import ViewReport from "../pages/Reports/components/ViewReport";
import RolesAndPermissions from "@/pages/RolesAndPermissions";
import StaffManagement from "@/pages/StaffManagement";
import EditAddRolesAndPermissions from "@/pages/RolesAndPermissions/EditAdd";
import UserManagement from "@/pages/UserManagement";
import RoleAssignedUser from "@/pages/RolesAndPermissions/RoleAssignedUser";
import ProfileManagement from "@/pages/ProfileManagement";
import ProfileAssignedUsers from "@/pages/ProfileManagement/ProfileAssignedUsers";
import AuthRequired from "../components/AuthRequired";
import AuthRedirect from "../components/AuthRedirect";
import TierConfigurationModule from "@/pages/TierConfiguration";
import GeneralMastersLayout from "@/pages/GeneralMasters/Layout";
import TierConfigurationDetails from "@/pages/TierConfiguration/TierConfigurationDetails";
import UserManagementDetails from "@/pages/UserManagement/UserManagementDetails";
import ApprovedOnBoardingDocument from "@/pages/UserManagement/ApprovedOnBoardingDocument";
import CMSPage from "@/pages/CMSPage";
import AddCMSPage from "@/pages/CMSPage/add-cms-page";
import ApprovedOnBoardingDocumentShow from "@/pages/UserManagement/ApprovedOnBoardingDocumentShow";
import ApprovedOnBoardingDocumentChangeHistory from "@/pages/UserManagement/ApprovedOnBoardingDocumentChangeHistory";
import ViewRejectedDocument from "@/pages/UserManagement/UserManagementViewRejectedDocument";
import EditCMSPage from "@/pages/CMSPage/edit-cms-page";
import ClientOnBoarding from "@/pages/ClientOnBoarding";
import AddTierConfiguration from "@/pages/TierConfiguration/AddTierConfiguration";
import EditTierConfiguration from "@/pages/TierConfiguration/EditTierConfiguration";
import ViewTierConfiguration from "@/pages/TierConfiguration/ViewTierConfiguration";
import ToDo from "@/pages/ToDo";
import Contacts from "@/pages/Contacts";
import SupplierOnBoarding from "@/pages/SupplierOnBoarding";
import T4Dashboard from "@/pages/T4/Dashboard";
import Documents from "@/pages/Documents/index";
import CommonDocumentModuleInterface from "@/pages/T4/CommonDocumentModuleInterface";
import OnboardedUserDetails from "@/pages/UserManagement/components/OnboardedUserDetails";
import FilesTimeLine from "@/pages/T4/T4FilesTimeline/index";
import OnBoardingDocument from "@/pages/T4/T4OnBoardingDocument/index";
import ViewFinancialStatement from "@/pages/T4/T4OnBoardingDocument/ViewFinancialStatement";
import OnBordingChangeHistory from "@/pages/T4/T4OnBoardingDocument/OnBordingDocumentChangeHistory";
import LiveChatScreen from "@/pages/LiveChat/index";

import NestedComponent from "@/pages/Documents/components/NestedComponent";
import CompleteProfile from "@/pages/T4/CompleteProfile";
import ViewOnboardingDocuments from "@/pages/T4/T4OnBoardingDocument/ViewOnboardingDocuments";
import OnboardingDocumentDetail from "@/pages/T4/T4OnBoardingDocument/ViewOnboardingDocuments/OnboardingDocumentDetail";
import OnboardingDocumentHistory from "@/pages/T4/T4OnBoardingDocument/ViewOnboardingDocuments/OnboardingDocumentHistory";
import AddPeopleGroupLiveChat from "@/pages/LiveChat/GroupChat/AddPeopleGroupLiveChat";
import ManagementTeamsGroupMembers from "@/pages/LiveChat/GroupChat/ManagementTeamsGroupMembers";
import ViewOnboardingDocumentsTimeline from "@/pages/T4/T4OnBoardingDocument/ViewOnboardingDocumentsTimeline";
import T3ApprovalList from "@/pages/T4/T4OnBoardingDocument/ViewOnboardingDocuments/T3ApprovalList";
import MyDriveTable from "@/pages/Documents/components/MyDriveTable";
import T3ApprovedOnboardingDocumentList from "@/pages/T4/T4OnBoardingDocument/ViewOnboardingDocuments/T3ApprovedOnboardingDocumentList";
import T3ApprovedOnboardingDocumentView from "@/pages/T4/T4OnBoardingDocument/ViewOnboardingDocuments/T3ApprovedOnboardingDocumentList/DocumentView";
import TestUpload from "@/components/common/testComponents/TestUpload";
import DocumentView from "@/pages/Documents/components/DocumentView";
import CreateTermsSheet from "@/pages/Documents/components/TermSheetDocuments/TermsSheet/CreateTermsSheet";
import IMDocumentHistoryTable from "@/pages/Documents/components/IMDocuments/T3/IMDocumentHistoryTable";
import Notification from "@/pages/Notification";
import SignedDocuments from "@/pages/SignedDocuments/index";
import SignDocumentViewer from "@/pages/SignedDocuments/components/SignDocumentViewer";
import SignedApprovalPage from "@/pages/Documents/components/SignedStatus/SignedApprovalPage";
import SignedDeclinePage from "@/pages/Documents/components/SignedStatus/SignedDeclinePage";
import {
  BankPermissions,
  BasePermissions,
  ClientOnboardingPermissions,
  ClientTypesPermissions,
  CmsPermissions,
  CommodityPermissions,
  ContactsPermissions,
  DashboardPermissions,
  DebitorsPermissions,
  DocumentsPermissions,
  GeneralMastersPermissions,
  LiveChatPermissions,
  PaymentTermsPermissions,
  ProfessionsPermissions,
  ProfileManagementPermissions,
  ReportsPermissions,
  RolesPermissionsPermissions,
  SignPermissions,
  StaffManagementPermissions,
  TierConfigurationPermissions,
  TodoPermissions,
  UsersPermissions,
} from "@/constants/permission-modules/base-permissions";

export interface Route {
  path: string;
  element?: React.ReactNode;
  permissions?: Partial<BasePermissions>[];
  children?: Route[];
}

// Auth routes (login, signup, etc.) - redirect if already authenticated
const authRoutes: Route[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/sign-up",
    element: <SignUp />,
  },
  {
    path: "/forget-password",
    element: <ForgetPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/email-verification",
    element: <EmailVerification />,
  },
  {
    path: "/email-verification-success",
    element: <EmailVerificationSuccess />,
  },
  {
    path: "/phone-verification",
    element: <PhoneVerification />,
  },
];

// Public routes (accessible to all)
const publicRoutes: Route[] = [
  {
    path: "*",
    element: <NotFound />,
  },
  {
    path: "/document/sign/approved",
    element: <SignedApprovalPage />,
  },
  {
    path: "/document/sign/declined",
    element: <SignedDeclinePage />,
  },
];

// Protected routes (require authentication)
const protectedRoutes: Route[] = [
  {
    path: "/menu",
    element: <Dashboard />,
  },
  {
    path: "/my-account",
    element: <MyAccount />,
  },
  {
    path: "/edit-my-account",
    element: <EditMyAccount />,
  },
  // {
  //   path: "/change-password",
  //   element: <ChangePassword />,
  // },
  {
    path: "/set-new-password",
    element: <SetNewPassword />,
  },
  {
    path: "/staff-management",
    element: <StaffManagement />,
    permissions: [StaffManagementPermissions.VIEW_STAFF_MANAGEMENT],
  },
  {
    path: "/user-management/*",
    element: <UserManagement />,
    permissions: [UsersPermissions.VIEW_USERS_MANAGEMENT],
  },

  {
    path: "/roles-and-permissions",
    element: <RolesAndPermissions />,
    permissions: [RolesPermissionsPermissions.VIEW_ROLES_AND_PERMISSIONS],
  },
  {
    path: "/edit-add-roles-and-permissions/:id?",
    element: <EditAddRolesAndPermissions />,
    permissions: [RolesPermissionsPermissions.VIEW_ROLES_AND_PERMISSIONS],
  },
  {
    path: "/profile-management",
    element: <ProfileManagement />,
    permissions: [ProfileManagementPermissions.VIEW_PROFILE_MANAGEMENT],
  },
  {
    path: "/profile-assigned-users/:id",
    element: <ProfileAssignedUsers />,
    permissions: [ProfileManagementPermissions.VIEW_PROFILE_MANAGEMENT],
  },
  {
    path: "/tier-configuration",
    element: <TierConfigurationModule />,
    permissions: [TierConfigurationPermissions.VIEW_TIER_CONFIGURATION],
  },
  {
    path: "/general-masters",
    element: <GeneralMastersLayout />,
    permissions: [GeneralMastersPermissions.VIEW_GENERAL_MASTERS],
    children: [
      {
        path: "commodity",
        permissions: [CommodityPermissions.VIEW_COMMODITY],
      },
      {
        path: "profession",
        permissions: [ProfessionsPermissions.VIEW_PROFESSIONS],
      },
      {
        path: "debtor-type",
        permissions: [DebitorsPermissions.VIEW_DEBTOR_TYPE],
      },
      {
        path: "payment-terms",
        permissions: [PaymentTermsPermissions.VIEW_PAYMENT_TERMS],
      },
      {
        path: "client-type",
        permissions: [ClientTypesPermissions.VIEW_CLIENT_TYPES],
      },
      {
        path: "bank",
        permissions: [BankPermissions.VIEW_BANK],
      },
    ],
  },
  {
    path: "/tier-configuration-details",
    element: <TierConfigurationDetails />,
    permissions: [TierConfigurationPermissions.VIEW_TIER_CONFIGURATION],
  },
  {
    path: "/user-management-details/:id",
    element: <UserManagementDetails />,
    permissions: [UsersPermissions.VIEW_USERS_MANAGEMENT],
  },
  {
    path: "/cms-page",
    element: <CMSPage />,
    permissions: [CmsPermissions.VIEW_CMS],
  },
  {
    path: "/add-cms-page",
    element: <AddCMSPage />,
    permissions: [CmsPermissions.CREATE_CMS],
  },
  {
    path: "/edit-cms-page/:id",
    element: <EditCMSPage />,
    permissions: [CmsPermissions.UPDATE_CMS],
  },
  {
    path: "/client-onboarding/*",
    element: <ClientOnBoarding />,
    permissions: [ClientOnboardingPermissions.VIEW_CLIENT_ONBOARDING],
  },
  {
    path: "/add-tier-configuration",
    element: <AddTierConfiguration />,
    permissions: [TierConfigurationPermissions.CREATE_TIER_CONFIGURATION],
  },
  {
    path: "/edit-tier-configuration",
    element: <EditTierConfiguration />,
    permissions: [TierConfigurationPermissions.UPDATE_TIER_CONFIGURATION],
  },
  {
    path: "/view-tier-configuration",
    element: <ViewTierConfiguration />,
    permissions: [TierConfigurationPermissions.VIEW_TIER_CONFIGURATION],
  },
  {
    path: "/to-do",
    element: <ToDo />,
    permissions: [TodoPermissions.VIEW_TO_DO],
  },
  {
    path: "/contacts",
    element: <Contacts />,
    permissions: [ContactsPermissions.VIEW_CONTACTS],
  },
  {
    path: "/dashboard",
    element: <T4Dashboard />,
    permissions: [DashboardPermissions.VIEW_DASHBOARD],
  },
  {
    path: "/complete-profile",
    element: <CompleteProfile />,
    permissions: [DashboardPermissions.VIEW_DASHBOARD],
  },
  {
    path: "/t4/onboarding-profile",
    element: <CommonDocumentModuleInterface />,
    permissions: [DashboardPermissions.VIEW_DASHBOARD],
  },
  {
    path: "/t4/view-onboarding-documents",
    element: <ViewOnboardingDocuments />,
    permissions: [DashboardPermissions.VIEW_DASHBOARD],
  },
  {
    path: "/t3/view-onboarding-documents/:id",
    element: <T3ApprovalList />,
    permissions: [ClientOnboardingPermissions.VIEW_CLIENT_ONBOARDING],
  },
  {
    path: "/t3/view-approved-onboarding-documents/:id",
    element: <T3ApprovedOnboardingDocumentList />,
    permissions: [ClientOnboardingPermissions.VIEW_CLIENT_ONBOARDING],
  },
  {
    path: "/t4/view-onboarding-document/view/:id",
    element: <OnboardingDocumentDetail />,
    permissions: [
      DashboardPermissions.VIEW_DASHBOARD,
      ClientOnboardingPermissions.VIEW_CLIENT_ONBOARDING,
    ],
  },
  {
    path: "/t4/view-onboarding-document/view-approved/:id",
    element: <T3ApprovedOnboardingDocumentView />,
    permissions: [ClientOnboardingPermissions.VIEW_CLIENT_ONBOARDING],
  },
  {
    path: "/t4/view-onboarding-document/history/:id",
    element: <OnboardingDocumentHistory />,
    permissions: [
      DashboardPermissions.VIEW_DASHBOARD,
      ClientOnboardingPermissions.VIEW_CLIENT_ONBOARDING,
    ],
  },
  {
    path: "/t4/view-onboarding-documents-timeline",
    element: <ViewOnboardingDocumentsTimeline />,
    permissions: [DashboardPermissions.VIEW_DASHBOARD],
  },
  {
    path: "/onboarded-user-details/:id",
    element: <OnboardedUserDetails />,
    permissions: [UsersPermissions.VIEW_USERS_MANAGEMENT],
  },
  {
    path: "/document",
    element: <Documents />, // Documents handles child views
    children: [
      {
        path: "to-be-approved",
        permissions: [DocumentsPermissions.VIEW_DOCUMENTS_TO_BE_APPROVED],
      },
      {
        path: "im-document",
        permissions: [DocumentsPermissions.VIEW_IM_DOCUMENTS],
      },
      {
        path: "terms-sheet",
        permissions: [DocumentsPermissions.VIEW_TERMS_SHEET_DOCUMENTS],
      },
      {
        path: "my-drive",
        permissions: [DocumentsPermissions.VIEW_MY_DRIVE_DOCUMENTS],
      },
    ],
  },
  {
    path: "/document/my-drive/:id",
    element: <MyDriveTable />, // Load this separately for MyDriveTable
    permissions: [DocumentsPermissions.VIEW_MY_DRIVE_DOCUMENTS],
  },
  {
    path: "/view-im-ts-document/:id",
    element: <DocumentView />,
    permissions: [
      DocumentsPermissions.VIEW_IM_DOCUMENTS,
      DocumentsPermissions.VIEW_TERMS_SHEET_DOCUMENTS,
    ],
  },
  {
    path: "/view-signed-document/:id",
    element: <DocumentView isSignedDocument={true} />,
    permissions: [SignPermissions.VIEW_SIGN],
  },
  {
    path: "/live-chat",
    element: <LiveChatScreen />,
    permissions: [LiveChatPermissions.VIEW_LIVE_CHAT],
  },
  {
    path: "/reports",
    element: <Reports />,
    permissions: [ReportsPermissions.VIEW_REPORTS],
  },
  {
    path: "/reports/add",
    element: <AddReport />,
    permissions: [ReportsPermissions.CREATE_REPORTS],
  },
  {
    path: "/reports/edit/:id",
    element: <EditReport />,
    permissions: [ReportsPermissions.UPDATE_REPORTS],
  },
  {
    path: "/reports/view/:id",
    element: <ViewReport />,
    permissions: [ReportsPermissions.VIEW_REPORTS],
  },
  {
    path: "/add-people-group-live-chat/:id",
    element: <AddPeopleGroupLiveChat />,
    permissions: [LiveChatPermissions.UPDATE_LIVE_CHAT],
  },
  {
    path: "/management-teams-group-members/:id",
    element: <ManagementTeamsGroupMembers />,
    permissions: [LiveChatPermissions.UPDATE_LIVE_CHAT],
  },
  {
    path: "/s3-upload",
    element: <TestUpload />,
  },
  {
    path: "/document/im-document/create-ts-document/:id",
    element: <CreateTermsSheet />,
    permissions: [DocumentsPermissions.CREATE_TERMS_SHEET_DOCUMENTS],
  },
  {
    path: "/document/im-document/history/:id/:type",
    element: <IMDocumentHistoryTable />,
    permissions: [DocumentsPermissions.VIEW_IM_DOCUMENTS],
  },
  {
    path: "/notification",
    element: <Notification />,
  },
  {
    path: "/signed-documents",
    element: <SignedDocuments />,
    permissions: [SignPermissions.VIEW_SIGN],
  },
];

// Router configuration
export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      // Auth routes wrapped with AuthRedirect
      ...authRoutes.map((route) => ({
        ...route,
        element: <AuthRedirect>{route.element}</AuthRedirect>,
      })),
      // Public routes as-is
      ...publicRoutes,
      // Protected routes wrapped with AuthRequired
      ...protectedRoutes.map((route) => {
        return {
          ...route,
          element: <AuthRequired route={route}>{route.element}</AuthRequired>,
        };
      }),
    ],
  },
]);

export default router;
